import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DebtType } from "../../Models/Credit";
import { LinkPaymentStatus } from "../../Models/LinkPaymentStatus";
import { PaymentType } from "../../Models/PaymentType";
import { ROUTE_PATHS } from "../../Routes/constants";
import { bulkAndSinglePaymentLinkGenerate } from "../../Services/BankService";
import {
  BulkSnoozeDatesResponse,
  BulkSnoozePreviewResponse,
  PaymentPlanSummary,
  buildBulkSnoozePreviewFromCatalogAndSelection,
  previewSnooze,
  stripPaymentPlanSummaryClientFields,
} from "../../Services/SnoozeService";
import { sendEvent } from "../../Utils/analytics";
import { formatLocalDateYYYYMMDD } from "../../Utils/formatLocalDate";
import { registerPaymentFrameCallbacks } from "../../Utils/paymentFrameCallbacks";
import BulkSnoozeDateSelectionModal from "./BulkSnoozeDateSelectionModal";
import BulkSnoozePaymentSelectionModal from "./BulkSnoozePaymentSelectionModal";
import BulkSnoozeSummaryModal from "./BulkSnoozeSummaryModal";

export interface BulkSnoozeFlowProps {
  /** Increment to start the flow (opens the date-selection modal). */
  startSignal: number;
  /** Payments in scope: all pending payments, or the multi-select subset. */
  payments: DebtType[];
  snoozeAvailability?: BulkSnoozeDatesResponse;
}

/**
 * CRL-3241: the bulk snooze flow (date selection → summary → optional payment
 * selection → pay), extracted from TotalDebts so the multi-select bar can run
 * the same flow with only the selected payments (new-design.html `openDefer`).
 * Logic is unchanged — it was moved verbatim from TotalDebts.
 */
const BulkSnoozeFlow = ({
  startSignal,
  payments,
  snoozeAvailability,
}: BulkSnoozeFlowProps) => {
  const navigate = useNavigate();

  const [bulkSnoozeDateSelectionModalOpen, setBulkSnoozeDateSelectionModalOpen] = useState<boolean>(false);
  const [bulkSnoozePaymentSelectionOpen, setBulkSnoozePaymentSelectionOpen] = useState(false);
  const [bulkSnoozeSummaryOpen, setBulkSnoozeSummaryOpen] = useState(false);
  /** Live preview — updated by payment selection modal API calls. */
  const [bulkSnoozePreview, setBulkSnoozePreview] = useState<BulkSnoozePreviewResponse | null>(null);
  /** Snapshot after date confirm only — summary modal always shows this, not payment-selection updates. */
  const [bulkSnoozeSummaryPreview, setBulkSnoozeSummaryPreview] =
    useState<BulkSnoozePreviewResponse | null>(null);
  const [bulkSnoozeSnoozeDate, setBulkSnoozeSnoozeDate] = useState<string>("");
  const [snoozeSelectedPlanIds, setSnoozeSelectedPlanIds] = useState<number[]>([]);
  /** First full `paymentPlans` from API — source of truth for list rows (backend omits unselected later). */
  const bulkSnoozePlanCatalogRef = useRef<PaymentPlanSummary[] | null>(null);
  /** Summary pay: previewId from first bulk preview after date confirm only (unchanged by payment-selection previews). */
  const bulkSnoozeInitialPreviewIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (startSignal > 0) {
      setBulkSnoozeDateSelectionModalOpen(true);
    }
  }, [startSignal]);

  const applyBulkSnoozePreviewUpdate = (
    apiPreview: BulkSnoozePreviewResponse,
    selectedPaymentPlanIds: number[]
  ) => {
    const catalog = bulkSnoozePlanCatalogRef.current;
    if (catalog?.length) {
      setBulkSnoozePreview(
        buildBulkSnoozePreviewFromCatalogAndSelection(
          catalog,
          apiPreview,
          selectedPaymentPlanIds
        )
      );
    } else {
      setBulkSnoozePreview(apiPreview);
    }
  };

  const onPayPressed = async (
    paymentPlanIds: number[],
    source: "summary" | "paymentSelection"
  ) => {
    const checkoutPreviewId =
      source === "summary"
        ? bulkSnoozeInitialPreviewIdRef.current
        : bulkSnoozePreview?.previewId;
    if (
      !checkoutPreviewId ||
      !bulkSnoozeSnoozeDate.trim() ||
      paymentPlanIds.length === 0
    ) {
      return;
    }
    try {
      const response = await bulkAndSinglePaymentLinkGenerate({
        type: PaymentType.BULK_SNOOZE,
        previewId: checkoutPreviewId,
      });
      if (response) {
        const callbackId = crypto.randomUUID();
        const amountsForAnalytics =
          source === "summary"
            ? bulkSnoozeSummaryPreview
            : bulkSnoozePreview;
        registerPaymentFrameCallbacks(callbackId, {
          onBackTriggered: () => {
            void logClickBackButtonPayCreditCardPage();
          },
          onFinish: (status: LinkPaymentStatus) => {
            const success = status === LinkPaymentStatus.PAID;
            const date = new Date(bulkSnoozeSnoozeDate);
            const statusText = status.toString();
            void logViewBulkSnoozeResultPage(
              date,
              amountsForAnalytics?.totalAmount ?? 0,
              amountsForAnalytics?.totalSnoozeFee ?? 0,
              success,
              success ? '' : statusText,
            );
          },
        });
        navigate(ROUTE_PATHS.PAYMENT_FRAME, {
          state: {
            url: response.url,
            token: response.token,
            referrer: ROUTE_PATHS.DEFAULT_PAYMENTS,
            type: PaymentType.BULK_SNOOZE,
            callbackId,
          },
        });
        logViewPayCreditCardPage(
          amountsForAnalytics?.totalAmount ?? 0,
          amountsForAnalytics?.totalSnoozeFee ?? 0,
          amountsForAnalytics?.calculatedPaymentPlanCount ?? 0,
          snoozeSelectedPlanIds.length ?? 0,
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const logClickBackButtonBulkSnoozeCalendarPage = async (
    paymentPlansCount: number,
  ) => {
    sendEvent('click_back_button_bulk_snooze_calendar_page', {
      total_credit_count: paymentPlansCount,

    });
  }

  const logViewBulkSnoozeSummaryPage = async (
    newDueDate: Date,
    snoozeCreditCount: number,
    totalSnoozedAmount: number,
    serviceFee: number,
  ) => {
    sendEvent('view_bulk_snooze_summary_page', {
      new_due_date:
        newDueDate.toISOString(),
      snoozable_credit_count:
        snoozeCreditCount,
      total_amount_of_debt_to_be_snoozed:
        totalSnoozedAmount,
      total_snooze_fee_amount:
        serviceFee,
    },
    );
  }

  const logClickBackButtonBulkSnoozeSummaryPage = async (
    selectedDate: Date,
    totalSnoozedAmount: number,
    serviceFee: number,
  ) => {

    sendEvent('click_back_button_bulk_snooze_summary_page',
      {
        selected_date:
          selectedDate.toISOString(),
        total_amount_of_debt_to_be_snoozed:
          totalSnoozedAmount,
        total_snooze_fee_amount:
          serviceFee,
      },
    );
  }

  const logViewPayCreditCardPage = async (
    totalSnoozeAmount: number,
    serviceFee: number,
    totalSnoozableCreditCount: number,
    selectedSnoozeableCreditCount?: number,
  ) => {
    sendEvent('view_pay_credit_card_page',
      {
        total_amount_of_debt_to_be_snoozed:
          totalSnoozeAmount,
        total_snooze_fee_amount:
          serviceFee,
        total_snoozable_credit_count:
          totalSnoozableCreditCount,
        selected_snoozeable_credit_count: selectedSnoozeableCreditCount,
      },
    );
  }

  const logViewSnoozableCreditList = async (
    snoozableCreditCount: number,
    snoozebleCreditAmount: number,
    notSnoozableCreditCount: number,
  ) => {
    sendEvent('view_snoozable_credit_list', {
      snoozable_credit_count:
        snoozableCreditCount,
      snoozeble_credit_amount:
        snoozebleCreditAmount,
      not_snoozable_credit_count:
        notSnoozableCreditCount,
    },
    );
  }

  const logClickBackButtonPayCreditCardPage = async () => {
    sendEvent('click_back_button_pay_credit_card_page', {
    });
  }

  const logViewBulkSnoozeResultPage = async (
    newDueDate: Date,
    totalAmountOfDebtToBeSnoozed: number,
    snoozeAmount: number,
    isSuccess: boolean,
    failStatus: string,
  ) => {
    sendEvent('view_bulk_snooze_result_page', {
      status_success: (isSuccess ? 'true' : 'false'),
      new_due_date:
        newDueDate.toISOString(),
      snoozeAmount:
        snoozeAmount,
      total_amount_of_debt_to_be_snoozed:
        totalAmountOfDebtToBeSnoozed,
      result_fail: failStatus,
    },
    );
  }

  return (
    <>
      {snoozeAvailability !== undefined && (
        <BulkSnoozeDateSelectionModal
          open={bulkSnoozeDateSelectionModalOpen}
          onClosed={() => {
            setBulkSnoozeDateSelectionModalOpen(false);
            setBulkSnoozeSnoozeDate("");
            logClickBackButtonBulkSnoozeCalendarPage(
              payments?.length ?? 0,
            );
          }
          }
          initialSelectedDateKey={
            bulkSnoozeSnoozeDate.trim() ? bulkSnoozeSnoozeDate : undefined
          }
          onPressed={async (selectedDate) => {
            setBulkSnoozeDateSelectionModalOpen(false);
            const snoozeDate = formatLocalDateYYYYMMDD(selectedDate);
            setBulkSnoozeSnoozeDate(snoozeDate);
            const paymentPlanIds = payments?.map((p) => p.id) ?? [];
            const preview = await previewSnooze({ snoozeDate, paymentPlanIds });
            if (preview) {
              const catalog = stripPaymentPlanSummaryClientFields(preview.paymentPlans);
              bulkSnoozePlanCatalogRef.current = catalog;
              bulkSnoozeInitialPreviewIdRef.current = preview.previewId;
              const eligibleIds = catalog
                .filter((p) => p.snoozeAvailable)
                .map((p) => p.paymentPlanId);
              const initialPreview = buildBulkSnoozePreviewFromCatalogAndSelection(
                catalog,
                preview,
                eligibleIds
              );
              setBulkSnoozePreview(initialPreview);
              setBulkSnoozeSummaryPreview(initialPreview);
              setSnoozeSelectedPlanIds([]);
              setBulkSnoozeSummaryOpen(true);
              logViewBulkSnoozeSummaryPage(selectedDate,
                preview.paymentPlans.length,
                preview.totalAmount,
                preview.totalSnoozeFee,
              );
            }
          }}
          startDate={new Date(snoozeAvailability.startDate)}
          endDate={new Date(snoozeAvailability.endDate)}
          paymentDates={payments?.map((p) => p.dueDate) ?? []}
          payments={payments}
        />
      )}
      {bulkSnoozePreview && (
        <BulkSnoozePaymentSelectionModal
          open={bulkSnoozePaymentSelectionOpen}
          snoozeDate={bulkSnoozeSnoozeDate}
          paymentPlans={bulkSnoozePreview.paymentPlans}
          serviceFeeFromPreview={bulkSnoozePreview.totalSnoozeFee}
          initialSelectedPaymentPlanIds={
            snoozeSelectedPlanIds.length > 0 ? snoozeSelectedPlanIds : undefined
          }
          onPreviewUpdated={applyBulkSnoozePreviewUpdate}
          onClose={() => {
            setBulkSnoozeDateSelectionModalOpen(false)
            setBulkSnoozePaymentSelectionOpen(false);
            setBulkSnoozeSummaryOpen(false);
            setBulkSnoozeSnoozeDate("");
          }}
          onBack={() => {
            setSnoozeSelectedPlanIds(bulkSnoozePreview?.paymentPlans.map((p) => p.paymentPlanId) ?? []);
            setBulkSnoozePaymentSelectionOpen(false);
            setBulkSnoozeSummaryOpen(true);
          }}
          onPayPressed={(ids) => onPayPressed(ids, "paymentSelection")}
        />
      )}
      {bulkSnoozeSummaryPreview && (
        <BulkSnoozeSummaryModal
          open={bulkSnoozeSummaryOpen}
          onClose={() => {
            setBulkSnoozeSummaryOpen(false);
            setBulkSnoozePreview(null);
            setBulkSnoozeSummaryPreview(null);
            bulkSnoozePlanCatalogRef.current = null;
            bulkSnoozeInitialPreviewIdRef.current = null;
            setSnoozeSelectedPlanIds([]);
            setBulkSnoozeSnoozeDate("");
          }}
          onBack={() => {
            setBulkSnoozeSummaryOpen(false);
            setBulkSnoozePreview(null);
            setBulkSnoozeSummaryPreview(null);
            bulkSnoozePlanCatalogRef.current = null;
            bulkSnoozeInitialPreviewIdRef.current = null;
            setSnoozeSelectedPlanIds(
              bulkSnoozePreview?.paymentPlans.map((p) => p.paymentPlanId) ?? []
            );
            setBulkSnoozeDateSelectionModalOpen(true);
            logClickBackButtonBulkSnoozeSummaryPage(
              new Date(bulkSnoozeSnoozeDate),
              bulkSnoozeSummaryPreview.totalAmount,
              bulkSnoozeSummaryPreview.totalSnoozeFee,
            );
          }}
          onEditPayments={() => {
            const snapshot = bulkSnoozeSummaryPreview;
            if (!snapshot) return;
            setBulkSnoozeSummaryOpen(false);
            setBulkSnoozePreview(snapshot);
            setSnoozeSelectedPlanIds([]);
            setBulkSnoozePaymentSelectionOpen(true);
            logViewSnoozableCreditList(
              snapshot.calculatedPaymentPlanCount ?? 0,
              snapshot.totalAmount ?? 0,
              snapshot.paymentPlans.filter((p) => !p.snoozeAvailable).length ?? 0,
            );
          }}
          paymentDate={bulkSnoozeSnoozeDate}
          preview={bulkSnoozeSummaryPreview}
          selectedPaymentPlanIds={
            snoozeSelectedPlanIds.length > 0 ? snoozeSelectedPlanIds : undefined
          }
          onPayPressed={(ids) => onPayPressed(ids, "summary")}
        />
      )}
    </>
  );
};

export default BulkSnoozeFlow;
